import { Map } from "immutable";
import moment from "moment";
import { createSelector } from "reselect";
import i18n from "~/i18n";
import Angie from "~/utils/angie.jsx";
import { currencySymbols } from "~/utils/currency_symbols.jsx";
import { formatDateSmart } from "~/utils/date.jsx";
import { formatDaysToRetirement } from "~/utils/formatDaysToRetirement.jsx";
import { formatFileSize, daysBetween } from "~/utils/utils.jsx";
import { getPersonalizedConfigOption } from "./selector.configOptions.jsx";
const getAccountSettings = (state) => state.accountSettings;
export const isClientPlusEnabled = (state) => state.config_options.get(`client_plus_enabled`);

// ACCOUNT PLAN SELECTORS
const defaultPlan = Map({});
const defaultStatus = Map({});
const defaultCurrentUsage = Map({});
const defaultPricingModel = Map({});

export const getAccountSettingsPlan = (state) => {
  return getAccountSettings(state).get(`plan`) || defaultPlan;
};
const getAccountSettingsStatus = (state) => {
  return getAccountSettings(state).get(`status`) || defaultStatus;
};
export const getPricingModel = (state) => {
  return getAccountSettings(state).get(`pricing_model`) || defaultPricingModel;
};

export const getPricingModelName = (state) => {
  return getPricingModel(state).get(`name`);
};

export const isLifetimePricingModel = createSelector(
  (state) => getPricingModel(state).get(`name`) || defaultPricingModel,
  (isLifetimePricingModel) => isLifetimePricingModel === "lifetime_2021"
);

export const isPerSeatPricingModel = createSelector(
  (state) => getPricingModel(state).get(`name`) || defaultPricingModel,
  (isPerSeatPricingModel) => isPerSeatPricingModel === "per_seat_2018"
);

export const isLegacyPricingModel = createSelector(
  (state) => getPricingModel(state).get(`name`) || defaultPricingModel,
  (isLegacyPricingModel) => isLegacyPricingModel === "plans_2013"
);

export const isFreePlan = createSelector(
  (state) => getAccountSettingsPlan(state).get(`name`) || defaultPlan,
  (isFreePlan) => isFreePlan === "free"
);

export const getAddOns = (state) => {
  return getPricingModel(state).get(`addons`);
};

export const getAccountSettingCurrentStorageUsage = createSelector(
  (state) => getAccountSettings(state).get(`current_usage`) || defaultCurrentUsage,
  (currentStorageUsed) => currentStorageUsed.get(`storage`)
);

export const getCurrentPlanStorageLimitation = createSelector(
  (state) => getAccountSettings(state).get(`plan`) || defaultPlan,
  (currentPlanLimitation) => currentPlanLimitation.get(`max_disc_space`)
);

export const getCurrentPlanUsersLimitation = createSelector(
  (state) => getAccountSettings(state).get(`limitations`) || defaultCurrentUsage,
  (currentPlanUsersLimitation) => currentPlanUsersLimitation.get(`max_members`)
);

export const getCurrentPlanVerboseName = createSelector(
  (state) => getAccountSettings(state).get(`plan`) || defaultPlan,
  (currentPlanVerboseName) => currentPlanVerboseName.get(`verbose_name`)
);

export const getAddOnsAsString = (state) => {
  let addOns = getAddOns(state).toJS();
  let addOnNames = [];

  for (let i = 0; i < addOns.length; i++) {
    if (addOns[i].is_enabled && addOns[i].is_bundle) addOnNames.push(`"` + addOns[0].verbose_name + `", `);
  }

  if (addOnNames.length > 0) addOnNames[addOnNames.length - 1] = addOnNames[addOnNames.length - 1].slice(0, -2);

  return addOnNames;
};

export const getPaidAddOns = (state) => {
  let addOns = getAddOns(state)?.toJS();
  let paidAddOns = [];

  for (let i = 0; i < addOns?.length; i++) {
    if (addOns[i].is_enabled) {
      paidAddOns.push(addOns[i]);
    }
  }

  return paidAddOns;
};

export const getAddOnByName = (state, addOnName) => {
  let paidAddOns = getPaidAddOns(state);
  let addOn = null;

  if (paidAddOns) {
    addOn = paidAddOns.filter((addOn) => {
      return addOn.name === addOnName;
    });
  }

  return addOn?.length > 0 ? addOn[0] : null;
};

export const isAddOnEnabled = (state, addOnName) => {
  let addOn = getAddOnByName(state, addOnName);

  return addOn ? addOn?.is_enabled : null;
};

const getAccountSettingsCurrentUsage = (state) => {
  return getAccountSettings(state).get(`current_usage`) || defaultCurrentUsage;
};

export const getMaxStorageForAccount = createSelector(getAccountSettingsCurrentUsage, (currentStorage) => {
  return currentStorage.get(`max_disk_space`);
});

export const getStorageUsed = createSelector(
  (state) => getAccountSettingsCurrentUsage(state).get(`storage_percent`),
  (state) => getAccountSettingsCurrentUsage(state).get(`storage_level`),
  (storage_percent = 0.0, storage_level = "none") => {
    return {
      storage_percent,
      storage_level,
    };
  }
);

export const getCurrentUsersCount = (state) => getAccountSettingsCurrentUsage(state).get(`users`);

export const getAccountPlanInfo = createSelector(
  (state) => getAccountSettingsPlan(state).get(`verbose_name`),
  (state) => getAccountSettingsPlan(state).get(`max_members`),
  (state) => getAccountSettingsPlan(state).get(`billing_period`),
  (state) => getAccountSettingsPlan(state).get(`price`),
  (state) => getAccountSettingsStatus(state).get(`is_trial`),
  (state) => getAccountSettingsStatus(state).get(`is_cancelled`),
  (state) => getAccountSettingsStatus(state).get(`is_suspended`),
  (state) => getAccountSettingsStatus(state).get(`is_activated`),
  (state) => getAccountSettingsStatus(state).get(`status_expires_at`),
  (state) => getAccountSettingsStatus(state).get(`next_billing_date`),
  (state) => getAccountSettingsStatus(state).get(`days_to_retirement`),
  (state) => getAccountSettingsCurrentUsage(state).get(`users`),
  (state) => getAccountSettingsCurrentUsage(state).get(`storage`),
  (state) => getAccountSettingsCurrentUsage(state).get(`max_disk_space`),
  (state) => getPersonalizedConfigOption(state, `format_date`),
  (state) => getAccountSettingsStatus(state).get(`discount`),
  (
    name = ``,
    max_members = 0,
    billing_period = `monthly`,
    price = 0,
    is_trial = true,
    is_cancelled = false,
    is_suspended = false,
    is_activated = false,
    status_expires_at = 0,
    next_billing_date = 0,
    days_to_retirement = null,
    users = 0,
    storage = 0,
    max_disk_space = 0,
    format = `%b %e. %Y`,
    discount = null
  ) => {
    storage = formatFileSize(storage, 2, true);
    if (is_trial || (!is_activated && is_suspended)) {
      let days = daysBetween(status_expires_at, moment.utc().unix());
      return {
        name: i18n.t("Trial"),
        members: users,
        nextBillDateLabel: i18n.t("Days Left in Free Trial"),
        nextBillDateValue: !is_activated && is_suspended ? 0 : days > 0 ? days : 0,
        price: i18n.t("Free"),
        priceLabel: i18n.t("Price"),
        storage,
        is_cancelled,
        is_suspended,
        is_activated,
        days_to_retirement,
        discount,
      };
    }

    let billingPeriod = `${billing_period}`.toLowerCase() === `monthly` ? "Monthly" : "Annual";
    max_disk_space = formatFileSize(max_disk_space, 0);
    const resolvedPrice = is_cancelled && is_activated ? `-` : `$${price}`;
    const resolvedNextBillDateValue =
      is_cancelled && is_activated
        ? `-`
        : formatDateSmart(next_billing_date, Angie.functions.phpToMomentDateFormat(format));

    return {
      name,
      members: `${users}/${max_members || `∞`}`,
      nextBillDateLabel: i18n.t("Next Billing Date"),
      nextBillDateValue: resolvedNextBillDateValue,
      price: resolvedPrice,
      priceLabel: i18n.t("Plan Price"),
      storage: `${storage}/${max_disk_space}`,
      billingPeriod,
      is_cancelled,
      is_suspended,
      is_activated,
      days_to_retirement,
      discount,
    };
  }
);

export const getCurrentPlanName = createSelector(getAccountSettingsPlan, (currentPlan) => currentPlan.get(`name`));

export const getCanChangePlan = createSelector(getAccountSettingsPlan, (canChangePlan) =>
  canChangePlan.get(`can_change_plan`)
);

export const getCurrentPlanPrice = createSelector(getAccountSettingsPlan, (currentPlan) => currentPlan.get(`price`));

export const getDaysToSuspension = createSelector(getAccountSettingsStatus, (accountStatus) =>
  accountStatus.get(`days_to_suspension`)
);

export const getCurrentBillingPeriod = createSelector(getAccountSettingsPlan, (currentPlan) =>
  currentPlan.get(`billing_period`)
);

export const getIsTrial = (state) => {
  const isTrial = getAccountSettingsStatus(state).get(`is_trial`);
  return typeof isTrial === `boolean` ? isTrial : true;
};

export const getDiscount = (state) => {
  return getAccountSettingsStatus(state).get(`discount`);
};

export const getBillingPeriodIsAnnually = (state) => {
  const isAnnually = getAccountSettingsPlan(state).get(`billing_period`);
  return isAnnually === `annually`;
};
export const getIsPerSeatPricingModel = (state) => {
  return getPricingModelName(state) === `per_seat_2018`;
};

export const getBillingPeriod = (state) => {
  return getAccountSettingsPlan(state).get(`billing_period`);
};

const defaultInvoice = Map({});

export const getLastInvoice = createSelector(
  (state) => getAccountSettings(state).get(`invoice`) || defaultInvoice,
  (invoice) => ({
    amount: invoice.amount ?? "-",
    issuedOn: invoice.issued_on ?? 0,
    currencyCode: invoice.currency_code ? currencySymbols[invoice.currency_code] : "",
    viewUrl: invoice.download_url || "#",
    invoiceLinkText: invoice.download_url ? i18n.t("View Invoice") : "-",
    isLinkEnabled: !!invoice.download_url,
  })
);

export const isLastInvoiceAvailable = createSelector(
  (state) => getAccountSettings(state).get(`invoice`) || defaultInvoice,
  (invoice) => !!invoice.amount
);

export const isAccountSuspended = (state) => {
  return getAccountSettingsStatus(state).get(`is_suspended`);
};

export const getBuyPlanButtonText = createSelector(
  (state) => getAccountSettingsCurrentUsage(state).get(`is_downgradable`, false),
  (isDowngradable) => {
    return isDowngradable ? i18n.t("Change Your Plan") : i18n.t("Upgrade Your Plan");
  }
);

export const getSubscriptionFooterText = createSelector(
  (state) => getAccountSettingsStatus(state).get(`is_suspended`),
  (state) => getAccountSettingsStatus(state).get(`is_activated`),
  (state) => getAccountSettingsStatus(state).get(`is_trial`),
  (is_suspended, is_activated, is_trial) => {
    return (is_suspended && !is_activated) || is_trial ? i18n.t("Cancel Trial Account") : i18n.t("Cancel Subscription");
  }
);

export const getIsAccountActived = createSelector(getAccountSettingsStatus, (accountSettingsStatus) =>
  accountSettingsStatus.get(`is_activated`)
);

export const getIsAccountSuspended = createSelector(getAccountSettingsStatus, (accountSettingsStatus) =>
  accountSettingsStatus.get(`is_suspended`)
);

export const getAccountStatus = createSelector(getAccountSettingsStatus, (accountSettingsStatus) =>
  accountSettingsStatus.get(`name`)
);

export const getIsSuspendedTrial = createSelector(
  (state) => getAccountSettingsStatus(state).get(`is_suspended`),
  (state) => getAccountSettingsStatus(state).get(`is_paid`),
  (is_suspended, is_paid) => {
    return is_suspended && !is_paid;
  }
);

export const getIsCanceled = createSelector(getAccountSettingsStatus, (accountSettingsStatus) =>
  accountSettingsStatus.get(`is_cancelled`)
);

export const getButtonPlanLabelText = createSelector(getIsAccountActived, (isActivated) => {
  return !isActivated ? i18n.t("Choose Plan") : i18n.t("Switch to");
});

export const getIsFailedPayment = createSelector(getAccountSettingsStatus, (accountSettingsStatus) =>
  accountSettingsStatus.get(`is_failed_payment`)
);

export const getAccountPlanTitle = createSelector(
  (state) => getAccountSettingsStatus(state).get(`is_cancelled`),
  (state) => getAccountSettingsStatus(state).get(`is_activated`),
  (state) => getAccountSettingsStatus(state).get(`days_to_retirement`),
  (state) => getAccountSettingsStatus(state).get(`is_suspended`),
  (isCanceled, isActivated, daysToRetirement, isSuspended) => {
    if (isSuspended && !isActivated) {
      return i18n.t("Trial is suspended." + formatDaysToRetirement(daysToRetirement));
    }
    if (isCanceled) return i18n.t("Subscription is cancelled." + formatDaysToRetirement(daysToRetirement));
    return i18n.t("Subscription");
  }
);

export const getAccountPlanClass = createSelector(
  (state) => getAccountSettingsStatus(state).get(`is_cancelled`),
  (state) => getAccountSettingsStatus(state).get(`is_trial`),
  (state) => getAccountSettingsStatus(state).get(`is_suspended`),
  (state) => getAccountSettingsStatus(state).get(`is_activated`),
  (state) => getAccountSettingsStatus(state).get(`is_failed_payment`),
  (isCanceled, isTrial, isSuspended, isActivated, isFailedPayment) => {
    if (isCanceled || (isSuspended && !isActivated)) return `subscription-cancelled`;
    if (isFailedPayment) return `subscription-failed-payment`;
    if (!isTrial) return `subscription-active`;
    return ``;
  }
);

export const getUpdatePaymentMethodLabel = createSelector(getIsAccountActived, (isActivated) => {
  return !isActivated ? i18n.t("Add Payment Method") : i18n.t("Update Payment Method");
});

export const getGetPaidPrices = createSelector(getAddOns, (addOns) => {
  const prices = {
    monthly: 0,
    annually: 0,
  };

  if (addOns) {
    const getPaidAddOn = addOns.find((addon) => addon.get("verbose_name") === "Get Paid");
    if (getPaidAddOn) {
      prices.monthly = getPaidAddOn.get("price").get("monthly");
      prices.annually = getPaidAddOn.get("price").get("annually");
    }
  }

  return prices;
});
